
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  onBeforeUpdate,
  watch
} from "vue";
import { useStore } from "@/store";
import portfolioProjects, { PortfolioProject } from "@/scripts/portfolio";
import { TouchEventListener, WheelEventListener } from "@/scripts/utils";

// selected class name
const selectedClass = "selected";

//
// move menu bar
//
const moveMenuBar = (menuBar: HTMLElement, projectItem: HTMLElement) => {
  const pageOffset = window.pageYOffset;
  const viewHeight = window.innerHeight;
  const itemHeight = projectItem.offsetHeight;
  const itemTop = projectItem.getBoundingClientRect().top;
  const menuTop = menuBar.getBoundingClientRect().top;
  const newTop = (viewHeight - itemHeight) / 2 - (itemTop - menuTop);
  const fontSize = getComputedStyle(document.documentElement).fontSize;
  const titleHeight = 0.8 * parseFloat(fontSize);
  const adjustTop =
    window.innerHeight <= window.innerWidth
      ? 0
      : (window.innerWidth * 1.1 * 0.8) / 3 - titleHeight;

  menuBar.style.top = `${pageOffset + newTop - adjustTop}px`;
};

// Project dictionary
interface ProjectDictionary {
  [title: string]: PortfolioProject[];
}

// definition of component
export default defineComponent({
  name: "PortfolioNavigation",
  props: {
    isNavigationEnabled: Boolean
  },
  setup: function(props) {
    // definition of Vuex
    const store = useStore();

    // ref to menu bar element
    const menuBarRef = ref<HTMLElement>();
    let projectItemRef: HTMLElement[] = [];
    const addProjectItemRef = (el: HTMLElement) => {
      projectItemRef.push(el);
    };

    // get group title class
    const groupClassName = (groupName: string) => {
      return groupName.startsWith("japanese") ? "jcrane" : "";
    };

    // map to dictionary
    const projectDictionary = portfolioProjects.reduce(
      (result: ProjectDictionary, current) => {
        if (!result[current.group.title]) {
          result[current.group.title] = [];
        }
        result[current.group.title].push(current);
        return result;
      },
      {}
    );

    // current selected project index
    let currentIndex = 0;

    // handle selected project change
    const changeSelectedProject = (newIndex: number, oldIndex: number) => {
      if (menuBarRef.value) {
        // remove selected class from the old selection
        projectItemRef[oldIndex].classList.remove(selectedClass);

        // move menu bar and add selected class to the new one
        const projectItem = projectItemRef[newIndex];
        moveMenuBar(menuBarRef.value, projectItem);
        projectItem.classList.add(selectedClass);

        // store the new index
        store.commit("setPortfolioProjectIndex", newIndex);

        // update the currentIndex
        currentIndex = newIndex;
      }
    };

    // for menu item selection change directly
    const jumpProject = (ev: MouseEvent) => {
      // find the index.
      const selectedIndex = projectItemRef.findIndex(i => i == ev.target);
      if (selectedIndex < 0) return;

      // immediately return when the same project is selected.
      if (currentIndex == selectedIndex) return;

      // change the selected project
      changeSelectedProject(selectedIndex, currentIndex);
    };

    // shift the selected project
    const shiftProject = (direction: number) => {
      // gets the old/new index
      const oldIndex = currentIndex;
      const newIndex = (() => {
        if (direction > 0 && currentIndex < projectItemRef.length - 1) {
          return oldIndex + 1;
        } else if (direction < 0 && currentIndex > 0) {
          return oldIndex - 1;
        }
        return oldIndex;
      })();

      // if no change (no direction), just return
      if (newIndex === oldIndex) return;

      // change selected project
      changeSelectedProject(newIndex, oldIndex);
    };

    // wheel event listener
    const wheelEventListener = new WheelEventListener(shiftProject);

    // touch event listener
    const touchEventListener = new TouchEventListener(
      TouchEventListener.Vertical,
      shiftProject
    );

    // on resized
    const resizeListener = () => {
      changeSelectedProject(currentIndex, currentIndex);
    };

    // handle wheel enabled event
    watch(
      () => props.isNavigationEnabled,
      newVal => {
        wheelEventListener.enable(newVal);
        touchEventListener.enable(newVal);
      }
    );

    // reset ref objects
    onBeforeUpdate(() => {
      projectItemRef = [];
    });

    // handler on mounted
    onMounted(() => {
      window.addEventListener("resize", resizeListener);
      wheelEventListener.enable(true);
      touchEventListener.enable(true);

      if (menuBarRef.value) {
        menuBarRef.value.style.visibility = "visible";
        changeSelectedProject(0, 0);
      }
    });

    // handler on unmounted
    onUnmounted(() => {
      //portfolioProjectIndex is updated by projectBelt
      window.removeEventListener("resize", resizeListener);
      wheelEventListener.enable(false);
      touchEventListener.enable(false);
    });

    return {
      menuBarRef,
      projectDictionary,
      addProjectItemRef,
      groupClassName,
      jumpProject
    };
  }
});
